/**
 * Processing Node Component
 *
 * Custom React Flow node for Processing types (conclusion)
 * Visual: Darker styling with distinct shape to differentiate from Evidence
 */

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { WorkspaceNodeData, getNodeTypeIcon, getNodeTypeColor } from '../types';

function ProcessingNode({ data, selected }: NodeProps<WorkspaceNodeData>) {
  const { nodeType, label, relevanceNote, referenceIds } = data;
  const color = getNodeTypeColor(nodeType);
  const icon = getNodeTypeIcon(nodeType);

  return (
    <div
      className={`
        rounded-xl border-2 shadow-lg
        transition-all duration-200
        min-w-[220px] max-w-[300px]
        ${selected ? 'ring-2 ring-amber-500 ring-offset-2' : ''}
      `}
      style={{
        backgroundColor: 'var(--primary)',
        borderColor: selected ? color : 'var(--primary-hover)'
      }}
    >
      {/* Input Handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-4 h-4 !bg-warning border-2 border-accent"
      />

      {/* Header */}
      <div
        className="px-4 py-2.5 rounded-t-xl flex items-center gap-2"
        style={{ backgroundColor: `${color}30` }}
      >
        {/* Type Badge */}
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm font-bold"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>

        {/* Type Label */}
        <span className="text-xs font-semibold uppercase tracking-wider text-warning">
          {nodeType === 'conclusion' ? 'Processing logic' : nodeType}
        </span>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        {/* Label */}
        <h3 className="text-sm font-bold text-white mb-2 line-clamp-2">
          {label}
        </h3>

        {/* Relevance Note */}
        <p className="text-xs text-fg-secondary line-clamp-4 leading-relaxed">
          {relevanceNote}
        </p>

        {/* Reference Links */}
        {referenceIds.length > 0 && (
          <div className="mt-3 pt-2 border-t border-border-default">
            <div className="flex flex-wrap gap-1">
              {referenceIds.slice(0, 3).map((refId) => (
                <span
                  key={refId}
                  className="text-xs px-2 py-0.5 rounded bg-accent-hover text-fg-secondary"
                >
                  {refId}
                </span>
              ))}
              {referenceIds.length > 3 && (
                <span className="text-xs text-fg-tertiary">
                  +{referenceIds.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Output Handle (bottom) - for potential chaining */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-4 h-4 !bg-warning border-2 border-accent"
      />
    </div>
  );
}

export default memo(ProcessingNode);
